import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, Autocomplete, Stack, Typography, Box, Button, DialogActions, TextField, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import SimpleAutocompleteProps from '../../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';
import { getBookingSubmitTypeName, getBookingSubmitTypes } from './BookingTypesUtils';
import { BookingTypeSubmitRules } from '../../../../utils/globalEnums';
import IntacctBookingTypeForm from './SubmitTypeForms/IntacctBookingTypeForm';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';
import QuickbooksBookingTypeForm from './SubmitTypeForms/QuickbooksBookingTypeForm';
import EmailBookingTypeForm from './SubmitTypeForms/EmailBookingTypeForm';
import ReliableBookingTypeForm from './SubmitTypeForms/ReliableBookingTypeForm';

BookingTypeForm.propTypes = {
  setBookingTypes: PropTypes.func,
  bookingType: PropTypes.object,
  close: PropTypes.func,
};

export default function BookingTypeForm({ bookingType, setBookingTypes, close }) {

  const [formValue, setFormValue] = useState(bookingType ? { id: bookingType.id, name: bookingType.name, submitTypeId: bookingType.submitTypeId } : {});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitTypes] = useState(getBookingSubmitTypes());

  useEffect(() => {
    if (bookingType)
    {
      let urlSegment = null;
      if (bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughIntacct) urlSegment = "BookingTypeIntacctData";
      else if (bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughQuickbooks) urlSegment = "BookingTypeQuickbooksData";
      else if (bookingType.submitTypeId === BookingTypeSubmitRules.sendFacilityEmail ||
        bookingType.submitTypeId === BookingTypeSubmitRules.sendThroughReliable) urlSegment = "BookingTypeValidationRules";
      if (urlSegment) {
        setLoading(true);
        axiosInstance.get(`Admin/InvoiceBookingType/${urlSegment}?id=${bookingType.id}`)
          .then(res => setFormValue(form => ({ ...form, ...(res.data ?? {}) })))
          .catch(() => enqueueSnackbar("Failed to load booking data", { variant: "error" }))
          .finally(() => setLoading(false));
      }
    }    
  }, [bookingType]);

  const handleSave = () => {
    setSaving(true);
    axiosInstance.post(`Admin/InvoiceBookingType/BookingTypeForm`, objectToFormData(formValue))
      .then(res => {
        res.data.submitType = getBookingSubmitTypeName(res.data.submitTypeId) 
        setBookingTypes(oldTypes => (formValue.id ? oldTypes.map(type => type.id === formValue.id ? res.data : type) : [...oldTypes, res.data]));
        enqueueSnackbar(`Booking type ${(formValue.id ? "updated" : "added")}`, { variant: "success" })
        close(res.data);
      })
      .catch(() => enqueueSnackbar("Failed to save booking type", { variant: "error" }))
      .finally(() => setSaving(false));
  };

  const setRule = (id, ruleValue) => setFormValue(value => ({
    ...value,
    rules: ruleValue ? [...(value.rules?.filter(rule => rule.id !== id) ?? []), ruleValue] : value.rules?.filter(rule => rule.id !== id)
  }));

  return <Dialog open onClose={(event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    close();
  }} maxWidth="md" fullWidth>
    <DialogTitle sx={{ textAlign: 'center' }} >
      {bookingType ? 'Edit' : 'Add'} Booking Account
      <CloseIconButton onClick={close} />
    </DialogTitle>
    <DialogContent sx={{ height: '100%', width: '100%' }} dividers>
      {loading ? <LoadingAnimation /> : <Box sx={{ width: '100%' }}>
        <Stack spacing={1}>
          <Box>
            <Typography variant="subtitle1">
              Submit type*
            </Typography>
            <Autocomplete
              {...SimpleAutocompleteProps}
              value={submitTypes.find(vendor => vendor.value === formValue.submitTypeId) ?? null}
              onChange={(_, newValue) => setFormValue(value => ({ ...value, submitTypeId: newValue.value }))}
              options={submitTypes}
            />
          </Box>
          <Tooltip title={<Box>
            <Box>The name given to this booking type. Will be used as the 'Sent To' value where applicable.</Box>
            <img src="https://res.cloudinary.com/agoraserv/image/upload/v1779111964/Website%20Images/c7d6abae-f0f0-4e35-a3cd-b2e314468941.png" alt="Invoice page submit popup" />
          </Box>} arrow placement="left">
            <Box>
              <Typography variant="subtitle1">
                Name*
              </Typography>
              <TextField
                size="small"
                autoComplete="off"
                value={formValue.name ?? ""}
                onChange={(e) => setFormValue(value => ({ ...value, name: e.target.value }))}
                fullWidth
              />
            </Box>
          </Tooltip>
          {formValue.submitTypeId === BookingTypeSubmitRules.sendThroughIntacct && <IntacctBookingTypeForm formValue={formValue} setFormValue={setFormValue} setRule={setRule} />}
          {formValue.submitTypeId === BookingTypeSubmitRules.sendThroughQuickbooks && <QuickbooksBookingTypeForm formValue={formValue} setFormValue={setFormValue} />}
          {formValue.submitTypeId === BookingTypeSubmitRules.sendFacilityEmail && <EmailBookingTypeForm formValue={formValue} setRule={setRule} />}
          {formValue.submitTypeId === BookingTypeSubmitRules.sendThroughReliable && <ReliableBookingTypeForm formValue={formValue} setRule={setRule} />}
        </Stack>
      </Box>}
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={close}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        color="primary"
        size="medium"
        disabled={!formValue.name || !formValue.submitTypeId || loading}
        sx={{ width: 150 }}
        onClick={handleSave}
        loading={saving}
      >
        {bookingType ? 'Save' : 'Add Account'}
      </LoadingButton>
    </DialogActions>
  </Dialog>;
}
